<template>
  <div class="gauges-container" v-if="contracts && contracts.length > 0">
    <div class="gauge-header">
      <h4>Análise de Consumo do Contrato</h4>
      <div class="contract-selector">
        <select v-model="selectedContract" @change="updateSelectedContract" class="form-select">
          <option v-for="(contract, index) in contracts" :key="index" :value="index">
            {{ contract.CN9_NUMERO }} - {{ contract.CNB_DESCRI }}
          </option>
        </select>
      </div>
    </div>

    <div class="gauges-grid">
      <div class="gauge-card">
        <div class="gauge-title">
          <h5>Volume Consumido</h5>
        </div>
        <div class="gauge-svg">
          <svg width="220" height="120" viewBox="0 0 220 120">
            <!-- Arco de fundo -->
            <path
              :d="describeArc(110, 110, 100, 90, 270)"
              stroke="#e5e7eb"
              stroke-width="18"
              fill="none"
            />
            <!-- Arco de valor -->
            <defs>
              <linearGradient :id="'grad-volume'" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stop-color="#60a5fa" />
                <stop offset="100%" stop-color="#3b82f6" />
              </linearGradient>
            </defs>
            <path
              :d="describeArc(110, 110, 100, 270, 270 + (volumePercentage * 1.8))"
              :stroke="'url(#grad-volume)'"
              stroke-width="18"
              stroke-linecap="round"
              fill="none"
            />
            <!-- Valor -->
            <text x="110" y="75" text-anchor="middle" font-size="2.5rem" font-weight="bold" fill="#1e293b">
              {{ volumePercentage.toFixed(1) }}%
            </text>
            <!-- Status -->
            <text x="110" y="100" text-anchor="middle" font-size="1.1rem" :fill="getStatusColor(volumePercentage)" font-weight="bold">
              {{ getStatusText(volumePercentage) }}
            </text>
          </svg>
        </div>
        <div class="gauge-details">
          <div class="detail-item">
            <span class="detail-label">Volume Total:</span>
            <span class="detail-value">{{ formatNumber(totalVolume) }} {{ volumeUnit }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Volume Consumido:</span>
            <span class="detail-value">{{ formatNumber(consumedVolume) }} {{ volumeUnit }}</span>
          </div>
        </div>
      </div>

      <div class="gauge-card">
        <div class="gauge-title">
          <h5>Prazo Consumido</h5>
        </div>
        <div class="gauge-svg">
          <svg width="220" height="120" viewBox="0 0 220 120">
            <path
              :d="describeArc(110, 110, 100, 90, 270)"
              stroke="#e5e7eb"
              stroke-width="18"
              fill="none"
            />
            <defs>
              <linearGradient :id="'grad-deadline'" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stop-color="#34d399" />
                <stop offset="100%" stop-color="#059669" />
              </linearGradient>
            </defs>
            <path
              :d="describeArc(110, 110, 100, 270, 270 + (deadlinePercentage * 1.8))"
              :stroke="'url(#grad-deadline)'"
              stroke-width="18"
              stroke-linecap="round"
              fill="none"
            />
            <text x="110" y="75" text-anchor="middle" font-size="2.5rem" font-weight="bold" fill="#1e293b">
              {{ deadlinePercentage.toFixed(1) }}%
            </text>
            <text x="110" y="100" text-anchor="middle" font-size="1.1rem" :fill="getStatusColor(deadlinePercentage)" font-weight="bold">
              {{ getStatusText(deadlinePercentage) }}
            </text>
          </svg>
        </div>
        <div class="gauge-details">
          <div class="detail-item">
            <span class="detail-label">Data Início:</span>
            <span class="detail-value">{{ formatDate(startDate) }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Data Fim:</span>
            <span class="detail-value">{{ formatDate(endDate) }}</span>
          </div>
        </div>
      </div>

      <div class="gauge-card">
        <div class="gauge-title">
          <h5>Valor Consumido</h5>
        </div>
        <div class="gauge-svg">
          <svg width="220" height="120" viewBox="0 0 220 120">
            <path
              :d="describeArc(110, 110, 100, 90, 270)"
              stroke="#e5e7eb"
              stroke-width="18"
              fill="none"
            />
            <defs>
              <linearGradient :id="'grad-value'" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stop-color="#fbbf24" />
                <stop offset="100%" stop-color="#ea580c" />
              </linearGradient>
            </defs>
            <path
              :d="describeArc(110, 110, 100, 270, 270 + (valuePercentage * 1.8))"
              :stroke="'url(#grad-value)'"
              stroke-width="18"
              stroke-linecap="round"
              fill="none"
            />
            <text x="110" y="75" text-anchor="middle" font-size="2.5rem" font-weight="bold" fill="#1e293b">
              {{ valuePercentage.toFixed(1) }}%
            </text>
            <text x="110" y="100" text-anchor="middle" font-size="1.1rem" :fill="getStatusColor(valuePercentage)" font-weight="bold">
              {{ getStatusText(valuePercentage) }}
            </text>
          </svg>
        </div>
        <div class="gauge-details">
          <div class="detail-item">
            <span class="detail-label">Valor Total:</span>
            <span class="detail-value">{{ formatCurrency(totalValue) }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Valor Consumido:</span>
            <span class="detail-value">{{ formatCurrency(consumedValue) }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="gauge-alert" v-if="showAlert">
      <font-awesome-icon :icon="['fas', 'exclamation-triangle']" />
      <span>{{ alertMessage }}</span>
    </div>
  </div>
  
  <div v-else class="no-contracts-message">
    <p>Nenhum contrato ativo disponível para análise.</p>
  </div>
</template>

<script>
export default {
  data() {
    return {
      selectedContract: 0,
      volumePercentage: 0,
      deadlinePercentage: 0,
      valuePercentage: 0,
      totalVolume: 0,
      consumedVolume: 0,
      volumeUnit: 'KG',
      startDate: null,
      endDate: null, 
      totalValue: 0,
      consumedValue: 0,
      showAlert: false,
      alertMessage: ''
    }
  },
  props: {
    contracts: {
      type: Array,
      required: true
    }
  },
  computed: {
    currentContract() {
      if (this.contracts && this.contracts.length > 0 && this.selectedContract >= 0) {
        return this.contracts[this.selectedContract];
      }
      return null;
    }
  },
  watch: {
    contracts: {
      immediate: true,
      handler() {
        if (this.contracts && this.contracts.length > 0) {
          this.selectedContract = 0;
          this.updateContractData();
        }
      }
    },
    selectedContract() {
      this.updateContractData();
    }
  },
  mounted() {
    this.updateContractData();
  },
  methods: {
    updateSelectedContract() {
      this.updateContractData();
    },
    updateContractData() {
      if (!this.currentContract) {
        this.resetData();
        return;
      }

      const contract = this.currentContract;
      
      // Processar dados de volume
      this.totalVolume = parseFloat(contract.CNB_QUANT || 0);
      this.consumedVolume = parseFloat(contract.CNB_QTDMED || 0);
      this.volumeUnit = contract.CNB_UM || 'KG';
      this.volumePercentage = this.totalVolume > 0 ? (this.consumedVolume / this.totalVolume) * 100 : 0;

      // Processar dados de valor
      this.totalValue = parseFloat(contract.CNB_VLTOT || 0);
      this.consumedValue = this.totalValue - parseFloat(contract.CNA_SALDO || 0);
      this.valuePercentage = this.totalValue > 0 ? (this.consumedValue / this.totalValue) * 100 : 0;

      // Processar dados de prazo
      this.startDate = this.parseDate(contract.CNA_DTINI);
      this.endDate = this.parseDate(contract.CN9_DTFIM);
      this.deadlinePercentage = this.calculateDeadlinePercentage();

      // Verificar alertas
      this.checkAlerts();
    },
    resetData() {
      this.volumePercentage = 0;
      this.deadlinePercentage = 0;
      this.valuePercentage = 0;
      this.totalVolume = 0;
      this.consumedVolume = 0;
      this.totalValue = 0;
      this.consumedValue = 0;
      this.startDate = null;
      this.endDate = null;
      this.showAlert = false;
      this.alertMessage = '';
    },
    parseDate(dateString) {
      if (!dateString) return null;
      
      // Formato esperado: DD/MM/YYYY
      const parts = dateString.split('/');
      if (parts.length === 3) {
        return new Date(parts[2], parts[1] - 1, parts[0]);
      }
      return null;
    },
    calculateDeadlinePercentage() {
      if (!this.startDate || !this.endDate) return 0;

      const now = new Date();
      const totalDays = (this.endDate - this.startDate) / (1000 * 60 * 60 * 24);
      const elapsedDays = (now - this.startDate) / (1000 * 60 * 60 * 24);
      
      const percentage = totalDays > 0 ? (elapsedDays / totalDays) * 100 : 0;
      return Math.max(0, Math.min(100, percentage)); // Entre 0 e 100
    },
    checkAlerts() {
      // Verificar se algum indicador está crítico
      const criticalThreshold = 80;
      const alerts = [];

      if (this.volumePercentage > criticalThreshold) {
        alerts.push('volume');
      }
      if (this.valuePercentage > criticalThreshold) {
        alerts.push('valor');
      }
      if (this.deadlinePercentage > criticalThreshold) {
        alerts.push('prazo');
      }

      if (alerts.length > 0) {
        this.showAlert = true;
        this.alertMessage = `Atenção: Consumo crítico de ${alerts.join(', ')} neste contrato.`;
      } else {
        this.showAlert = false;
        this.alertMessage = '';
      }
    },
    formatNumber(value) {
      if (value === null || value === undefined || isNaN(value)) {
        return '0';
      }
      return new Intl.NumberFormat('pt-BR').format(Number(value));
    },
    formatCurrency(value) {
      if (value === null || value === undefined || isNaN(value)) {
        return 'R$ 0,00';
      }
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(Number(value));
    },
    formatDate(date) {
      if (!date) return 'N/A';
      return new Intl.DateTimeFormat('pt-BR').format(date);
    },
    getStatusText(percentage) {
      if (percentage <= 50) return 'NORMAL';
      if (percentage <= 75) return 'ATENÇÃO';
      return 'CRÍTICO';
    },
    getStatusColor(percentage) {
      if (percentage <= 50) return "#059669";
      if (percentage <= 75) return "#ea580c";
      return "#dc2626";
    },
    polarToCartesian(centerX, centerY, radius, angleInDegrees) {
      var angleInRadians = (angleInDegrees-90) * Math.PI / 180.0;
      return {
        x: centerX + (radius * Math.cos(angleInRadians)),
        y: centerY + (radius * Math.sin(angleInRadians))
      };
    },
    describeArc(x, y, radius, startAngle, endAngle){
      var start = this.polarToCartesian(x, y, radius, startAngle);
      var end = this.polarToCartesian(x, y, radius, endAngle);
      var arcSweep = endAngle - startAngle <= 180 ? "0" : "1";
      var d = [
        "M", start.x, start.y,
        "A", radius, radius, 0, arcSweep, 1, end.x, end.y
      ].join(" ");
      return d;
    }
  }
}
</script>

<style scoped>
.gauges-container {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 1.5rem;
  margin-top: 1.5rem;
}

.gauge-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e2e8f0;
}

.gauge-header h4 {
  color: #1e293b;
  font-size: 1.25rem;
  font-weight: 500;
  margin: 0;
}

.contract-selector {
  width: 300px;
}

.form-select {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  background-color: white;
  color: #1e293b;
  font-size: 0.875rem;
}

.form-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
}

.gauges-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1rem;
}

.gauge-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 1.5rem;
  height: 100%;
}

.gauge-title {
  margin-bottom: 1rem;
}

.gauge-title h5 {
  color: #1e293b;
  font-size: 1.125rem;
  font-weight: 500;
  text-align: left;
  margin: 0;
}

.gauge-svg {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 0.5rem;
}

svg {
  display: block;
}

.gauge-details {
  margin-top: 1.5rem;
  background: #f8fafc;
  border-radius: 0.5rem;
  overflow: hidden;
}

.detail-item {
  padding: 0.75rem 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e2e8f0;
}

.detail-item:last-child {
  border-bottom: none;
}

.detail-label {
  color: #64748b;
  font-size: 0.875rem;
  font-weight: normal;
}

.detail-value {
  color: #1e293b;
  font-size: 0.875rem;
  font-weight: 500;
}

.gauge-alert {
  background-color: #fee2e2;
  border: none;
  color: #dc2626;
  padding: 0.75rem 1rem;
  border-radius: 0.375rem;
  margin-top: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
}

.gauge-alert svg {
  font-size: 1.25rem;
}

@media (max-width: 768px) {
  .gauges-container {
    padding: 1rem;
  }

  .gauge-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  .contract-selector {
    width: 100%;
  }

  .gauges-grid {
    grid-template-columns: 1fr;
  }

  .gauge-value {
    font-size: 1.25rem;
  }
}

@keyframes pulse {
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.05); opacity: 0.8; }
  100% { transform: scale(1); opacity: 1; }
}

.gauge-value {
  animation: pulse 2s ease-in-out infinite;
}

/* Ajuste do gradiente baseado no status */
.gauge[data-status="good"] .gauge-fill {
  background: linear-gradient(90deg, #34d399 0%, #059669 100%);
}

.gauge[data-status="warning"] .gauge-fill {
  background: linear-gradient(90deg, #fb923c 0%, #ea580c 100%);
}

.gauge[data-status="danger"] .gauge-fill {
  background: linear-gradient(90deg, #f87171 0%, #dc2626 100%);
}

.no-contracts-message {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 2rem;
  margin-top: 1.5rem;
  text-align: center;
}

.no-contracts-message p {
  color: #64748b;
  font-size: 1rem;
  margin: 0;
}
</style>